import { Component, OnInit } from '@angular/core';
import { ESectionTypes } from '../../../shared/models/eop_types.enum';
import { Icons } from '../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { FormsModule } from '@angular/forms';
import { IMaterial } from '../../../shared/models/materials';
import { MaterialsEquipmentDetailComponent } from './materials-equipment-detail/materials-equipment-detail.component';
import { NewMaterialsEquipmentComponent } from './new-materials-equipment/new-materials-equipment.component';
import { EditMaterialsEquipmentComponent } from './edit-materials-equipment/edit-materials-equipment.component';
import { MaterialEquipmentService } from '../../../shared/services/materialsEquipment.service';
import { ContactsService } from '../../../shared/services/contact.service';

@Component({
  selector: 'list-of-materials-equipment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    IntroductionComponent,
    SpinnerComponent,
    PopupMessageComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    NgxPaginationModule,
    MaterialsEquipmentDetailComponent,
    NewMaterialsEquipmentComponent, 
    EditMaterialsEquipmentComponent
  ],
  templateUrl: './list-of-materials-equipment.component.html',
  styleUrl: './list-of-materials-equipment.component.css'
})
export class ListOfMaterialsEquipmentComponent implements OnInit {
  eopSectionTypeId = ESectionTypes["List of Materials and Equipment"]

  loading: boolean = false;
  sortOrder: boolean = true;
  showModalType: string = '';

  icons = Icons;
  searchQuery: string = '';
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  fullMaterialsList: IMaterial[] = [];
  materialsList: IMaterial[] = [];
  messageModal: string | null = null;
  popupMessage: string = '';
  selectedMaterial: any | null = null;
  threatDetails: IMaterial | null = null;
  materialToEdit: IMaterial | null = null;
  page = 1;
  itemsPerPage = 10;
  fullContactsList: IContact[] = [];

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private materialEquipmentService: MaterialEquipmentService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadContacts();
  }  

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        console.log('Contacts loaded successfully:', response);
        this.loadMetarialsList();
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };

  loadMetarialsList(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.materialEquipmentService.getBuildingMaterials(this.currentBuildingId).subscribe({
        next: (res) => {
          console.log('Materials & Equipment loaded successfully:', res);
          if (!res.has_error) {
            this.fullMaterialsList = res.data;// Map materials + attach contact_name
            this.materialsList = this.fullMaterialsList.map((material: any) => {
              const contact = this.fullContactsList.find(c => c.id === material.contact_id);
              return {
                ...material,
                contact_name: contact ? contact.name : 'N/A'
              };
            });
          }
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message || 'Failed to load materials & equipment list. Please try again.';
        }
      })
    }
  }

  // sort threat-goal added to header of table
  sortList(key: keyof typeof this.materialsList[0]): void {
    this.sortOrder = !this.sortOrder;
    this.materialsList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  handleOpenModal(modalName: string, selectedItem?: any): void {
    this.showModalType = modalName;
    this.selectedMaterial = selectedItem ? selectedItem : null;
  }

  handleCloseModal(modalName?: string): void {
    this.showModalType = '';
    this.selectedMaterial = null;
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.handleCloseModal(actionType);
    this.loadMetarialsList();

    if (message.type === 'success') {
      this.messageModal = 'success';
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
    } 
    this.popupMessage = message.message;
  }

  approveMaterials(): void {
    if (this.currentBuildingId) {
      this.materialEquipmentService.approveBuildingMaterials(this.currentBuildingId).subscribe({
        next: (res) => {
          if (!res.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after confirming list of materials & equipment.';
            this.loadMetarialsList();
          }
        },
        error: (err) => {
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  }

  handleDelete(item: any): void {
    this.loading = true;
    if (this.currentBuildingId && item.id) {
      this.materialEquipmentService.deleteMaterial(this.currentBuildingId, item.id.toString()).subscribe({
        next: (res) => {
          this.loading = false;
          this.showModalType = '';
          if (!res.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after deleting a material & equipment.';
            this.loadMetarialsList();
          }
        },
        error: (err) => {
          this.loading = false;
          this.showModalType = '';
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  } 

  filterMaterialsList(): void {
    const query = this.searchQuery.toLowerCase().trim();
  
    this.materialsList = this.fullMaterialsList.filter((item) => {
      return (
        item.material?.toLowerCase().includes(query) ||
        item.location?.toLowerCase().includes(query) ||
        item.last_updated_user_name?.toLowerCase().includes(query) ||
        item.last_updated_at?.toLowerCase().includes(query) ||
        item.contact_name?.toString().toLowerCase().includes(query)
      );
    });
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
